import moment from "moment"
// FIXME: remove this disable directive and fix the ESLint error(s)
// @ts-expect-error: Flow to TS migration.
// eslint-disable-next-line no-restricted-imports
import _ from "lodash"
// FIXME: remove this disable directive and fix the ESLint error(s)
// @ts-expect-error: Flow to TS migration.
// eslint-disable-next-line no-restricted-imports
import fp from "lodash/fp"
// @ts-expect-error: Flow to TS migration.
import keymirror from "keymirror"
import { differenceInYears } from "date-fns"
import { Insurance } from "@kiwicom/frontend-common/components/CommonInsurance/services/utils"
import {
  DocumentId,
  PassengerId,
  PassengerName,
  PriceTotal,
  PassengerTitle,
} from "@kiwicom/frontend-common/types/primitiveTypes"
import { Translate } from "@kiwicom/frontend-common/NitroTranslate/services/translate"

const createInitial = fp.compose(fp.capitalize, fp.first)
const capitalizeLastName = fp.compose(fp.capitalize, fp.last, fp.split(" "))

const trimName = (name?: string | null) => (name ? name.trim() : "")

export const PASSENGER_CATEGORIES = keymirror({
  infant: null,
  child: null,
  teen: null,
  adult: null,
})

export type PassengerCategory = "infant" | "child" | "teen" | "adult"

type PBOrderedFlightData = {
  currency: string
  price: number
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line babel/camelcase
  is_ordered: boolean
}

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
type PBOrderedData = {
  [flightId: string]: PBOrderedFlightData
}

export type Document = {
  code: string | null | undefined
  expiration: moment.Moment | null | undefined
}

export type PassengerArgs = {
  id: number
  category: PassengerCategory
  title: PassengerTitle
  firstname: PassengerName
  middlename: PassengerName | null
  lastname: PassengerName
  nationality: string
  birth: moment.Moment
  document: Document
  insurance: Insurance
  insurancePrice: number
  bags: number // not used on MMB,
  baggage: {
    handBag: number
    holdBag: number
  }
  handBags: number // not used on MMB,
  extraHandBags: number
  hasOrderedPB: boolean
  priorityBoarding: PBOrderedData
}

export default class Passenger {
  id: PassengerId

  category: PassengerCategory

  title: PassengerTitle

  firstname: PassengerName

  middlename: PassengerName | null

  lastname: PassengerName

  nationality: string

  birth: moment.Moment

  document: {
    code: DocumentId | null | undefined
    expiration: moment.Moment | null | undefined
  }

  insurance: Insurance

  insurancePrice: PriceTotal

  bags: number

  baggage: {
    handBag: number
    holdBag: number
  }

  handBags: number

  extraHandBags: number

  // @ts-expect-error: Flow to TS migration.
  hasOrderedPB: boolean

  priorityBoarding: PBOrderedData

  constructor(args: PassengerArgs) {
    this.id = args.id

    this.category = args.category
    this.title = args.title
    this.firstname = trimName(args.firstname)
    this.middlename = trimName(args.middlename)
    this.lastname = trimName(args.lastname)
    this.nationality = args.nationality

    this.birth = args.birth
    this.document = args.document

    this.insurance = args.insurance
    this.insurancePrice = args.insurancePrice

    this.bags = args.bags
    this.baggage = args.baggage
    this.handBags = args.handBags
    this.extraHandBags = args.extraHandBags
    this.priorityBoarding = args.priorityBoarding
  }

  getFullName(): string {
    return Passenger.formatName(this)
  }

  getShortName(): string {
    return Passenger.formatShortName(this)
  }

  getInitials(): string {
    return Passenger.formatInitials(this)
  }

  getAge(): number {
    // @ts-expect-error: Flow to TS migration.
    return differenceInYears(new Date(), this.birth)
  }

  hasBagsAllowed(bagsAllowedForInfants: boolean) {
    const day = this.birth.date()
    const month = this.birth.month() + 1 // Unlike math, common people don't index from zero.
    const year = this.birth.year()

    return Passenger.hasBagsAllowedStatic(day, month, year, bagsAllowedForInfants)
  }

  // Type of passenger can be related to some milestone (e.g. journeyDepartureMoment) by default milestone = moment()
  // startOf("day") is really important
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static determineTypeFromInput(
    day: number,
    month: number,
    year: number,
    milestone: moment.Moment = moment().startOf("day"),
    {
      adult,
      teen,
      child,
    }: {
      adult?: number
      teen?: number
      child?: number
    } = {},
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    enableUndefined: boolean = false,
  ) {
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const adultThreshold = adult || 18
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const teenThreshold = teen || adultThreshold // Same as adult => no teen category
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const childThreshold = child || 2

    if (day && month && year) {
      const birthdayMoment = moment(`${year}-${month}-${day}`, "YYYY-MM-DD")

      if (!birthdayMoment.isValid() || year.toString().length < 4) {
        return PASSENGER_CATEGORIES.adult
      }

      const adultThresholdMoment = milestone.clone().subtract(adultThreshold, "years")
      const teenThresholdMoment = milestone.clone().subtract(teenThreshold, "years")
      const childThresholdMoment = milestone.clone().subtract(childThreshold, "years")

      /**
       * ADULT  >= adultThreshold or < now() - we do not have "invalid" passenger category
       * TEEN   < adultThreshold
       * CHILD  < teenThreshold
       * INFANT < childThreshold
       */

      if (birthdayMoment.isBefore(adultThresholdMoment) || birthdayMoment.isAfter(moment())) {
        return PASSENGER_CATEGORIES.adult
      }
      if (birthdayMoment.isBefore(teenThresholdMoment)) {
        return PASSENGER_CATEGORIES.teen
      }
      if (birthdayMoment.isBefore(childThresholdMoment)) {
        return PASSENGER_CATEGORIES.child
      }
      return PASSENGER_CATEGORIES.infant
    }

    if (enableUndefined) {
      return undefined
    }

    // Default type of passenger should be "adult"
    return PASSENGER_CATEGORIES.adult
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static determineAgeFromInput(day: number, month: number, year: number, NOW: Date = new Date()) {
    // numbers need to be in two digit format otherwise it doesn't work on non-chrome browsers
    // e.g. 4 -> 04
    return differenceInYears(NOW, new Date(`${year}/${month}/${day}`))
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static hasBagsAllowedStatic(
    day: number,
    month: number,
    year: number,
    bagsAllowedForInfants: boolean,
  ) {
    return (
      Passenger.determineTypeFromInput(day, month, year) !== PASSENGER_CATEGORIES.infant ||
      bagsAllowedForInfants
    )
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static translateTitle(title: PassengerTitle, passengerName: string, translate: Translate) {
    switch (title) {
      case "mr":
        // @ts-expect-error: Flow to TS migration.
        return translate(__("booking.passenger.title_options.mr"), { passengerName })
      case "ms":
        // @ts-expect-error: Flow to TS migration.
        return translate(__("booking.passenger.title_options.ms"), { passengerName })
      default:
        return title
    }
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static formatName(passenger: Passenger): string {
    const { firstname, middlename, lastname } = passenger

    // `_.compact` removes the middle name if null.
    return _.compact([firstname, middlename, lastname]).join(" ")
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static formatNameWithTitle(passenger: Passenger, translate: Translate): string {
    const { title } = passenger

    return Passenger.translateTitle(title, Passenger.formatName(passenger), translate)
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static formatShortName(passenger: Passenger): string {
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${createInitial(passenger.firstname)}. ${capitalizeLastName(passenger.lastname)}`
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static formatInitials(passenger: Passenger): string {
    // Fix this the next time the file is edited. (Flow to TS)
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${createInitial(passenger.firstname)}${createInitial(passenger.lastname)}`
  }

  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/member-ordering
  static fake(args: Partial<Passenger> = {}) {
    const defaults: PassengerArgs = {
      id: 123,
      category: "adult",
      title: "mr",
      firstname: "Test",
      middlename: "Testovic",
      lastname: "Tester",
      nationality: "ru",
      birth: moment("1990-01-01"),
      document: {
        code: "123ABC",
        expiration: moment("2020-01-01"),
      },
      insurance: "none",
      insurancePrice: 10,
      bags: 1,
      baggage: {
        handBag: 1,
        holdBag: 1,
      },
      handBags: 1,
      extraHandBags: 1,
      hasOrderedPB: false,
      priorityBoarding: {},
    }

    const merged: PassengerArgs = { ...defaults, ...args }

    return new Passenger(merged)
  }
}
